import api from "../http/api";

export const createMantenedora = async (mantenedora) => {
  try {
    const response = await api.client.post("api/v1/Mantenedoras", mantenedora);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const errorMessage = Array.isArray(error.response.data) 
        ? error.response.data[0] 
        : error.response.data;
      throw new Error(errorMessage || "Erro ao criar mantenedora");
    }
    throw new Error("Falha na conexão com a API");
  }
};

export const readMantenedora = async () => {
  try {
    const response = await api.client.get("api/v1/ReadMantenedoraByAdminId");
    return response.data;
  } catch (error) {
    return null;
  }
};

export const readInstitutionsByMantenedora = async () => {
  // Não engolir erros aqui: retornar { institutions: [] } em caso de falha
  // fazia os consumidores tratarem "API indisponível" como "mantenedora sem
  // instituições", limpando a lista persistida e sumindo com o seletor de
  // instituições do header. Os chamadores já possuem try/catch próprio.
  const response = await api.client.get("api/v1/ReadInstitutionsByMantenedoraId");
  return response.data;
};

/**
 * Busca todos os usuários da mantenedora (usuários da mantenedora + usuários das instituições)
 * @param {string} mantenedoraId - ID da mantenedora
 * @returns {Promise<Object>} - Dados dos usuários agrupados
 */
export const readMantenedoraUsers = async (mantenedoraId) => {
  try {
    const response = await api.client.get(`api/v1/mantenedoras/${mantenedoraId}/users`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar usuários da mantenedora:", error);
    throw error;
  }
};

/**
 * Cria um novo usuário da mantenedora
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {Object} userData - Dados do usuário
 * @returns {Promise<Object>} - Resultado da criação
 */
export const createMantenedoraUser = async (mantenedoraId, userData) => {
  try {
    const response = await api.client.post(`api/v1/mantenedoras/${mantenedoraId}/users`, userData);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const errorMessage = Array.isArray(error.response.data) 
        ? error.response.data[0] 
        : error.response.data;
      throw new Error(errorMessage || "Erro ao criar usuário");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Cria um novo usuário em uma instituição específica
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {string} institutionId - ID da instituição
 * @param {Object} userData - Dados do usuário
 * @returns {Promise<Object>} - Resultado da criação
 */
export const createInstitutionUser = async (mantenedoraId, institutionId, userData) => {
  try {
    const response = await api.client.post(
      `api/v1/mantenedoras/${mantenedoraId}/institutions/${institutionId}/users`, 
      userData
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const errorMessage = Array.isArray(error.response.data) 
        ? error.response.data[0] 
        : error.response.data;
      throw new Error(errorMessage || "Erro ao criar usuário");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Atualiza dados de um usuário no escopo da mantenedora
 * (usuário da mantenedora ou de uma instituição da mantenedora).
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {string} userId - ID do usuário a ser atualizado
 * @param {Object} userData - { firstName, lastName, email, phoneNumber?, role, institutionIds? }
 * @returns {Promise<Object>} - Resultado da atualização
 */
export const updateMantenedoraUser = async (mantenedoraId, userId, userData) => {
  try {
    const response = await api.client.put(
      `api/v1/mantenedoras/${mantenedoraId}/users/${userId}`,
      userData
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = Array.isArray(data)
        ? data[0]
        : (data.errors ? Object.values(data.errors).flat().join(" ") : data.error || data);
      throw new Error(errorMessage || "Erro ao atualizar usuário");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Define (ou limpa) a instituição primária de um usuário da mantenedora.
 * Essa instituição é acessada automaticamente no login, sem passar pela
 * tela de seleção de instituição.
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {string} userId - ID do usuário
 * @param {string|null} institutionId - ID da instituição primária, ou null para limpar
 * @returns {Promise<Object>} - Resultado da atualização
 */
export const updatePrimaryInstitution = async (mantenedoraId, userId, institutionId) => {
  try {
    const response = await api.client.put(
      `api/v1/mantenedoras/${mantenedoraId}/users/${userId}/primary-institution`,
      { institutionId: institutionId || null }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = Array.isArray(data)
        ? data[0]
        : (data.error || data.errors ? Object.values(data.errors || {}).flat().join(" ") : data);
      throw new Error(errorMessage || "Erro ao definir instituição primária");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Exclui um usuário no escopo da mantenedora (usuário da mantenedora ou
 * de uma instituição da mantenedora).
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {string} userId - ID do usuário a ser excluído
 * @returns {Promise<Object>} - Resultado da exclusão
 */
export const deleteMantenedoraUser = async (mantenedoraId, userId) => {
  try {
    const response = await api.client.delete(
      `api/v1/mantenedoras/${mantenedoraId}/users/${userId}`
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = Array.isArray(data)
        ? data[0]
        : (data.error || data.errors ? Object.values(data.errors || {}).flat().join(" ") : data);
      throw new Error(errorMessage || "Erro ao excluir usuário");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Envia convite por e-mail para novo usuário concluir cadastro (link temporário).
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {Object} payload - { email, role, institutionId? }
 * @returns {Promise<Object>} - { inviteId, expiresAt }
 */
export const sendUserInvite = async (mantenedoraId, payload) => {
  try {
    const response = await api.client.post(
      `api/v1/mantenedoras/${mantenedoraId}/users/invite`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const errors = error.response.data;
      const message = Array.isArray(errors) ? errors.join(" ") : (errors.message || errors);
      throw new Error(message || "Erro ao enviar convite");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Busca a mantenedora do usuário logado (id, nome, plano atual) junto com o
 * catálogo de planos ativos disponíveis para contratação.
 * @returns {Promise<Object|null>} - { id, name, planId, plans: [...] }
 */
export const getMantenedoraWithPlans = async () => {
  try {
    const response = await api.client.get("api/v1/mantenedoras/me/with-plans");
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar mantenedora e planos:", error);
    return null;
  }
};

/**
 * Formaliza a contratação de um plano durante o onboarding.
 * Registra PlanId e PlanContractedAt; a equipe entrará em contato em até 7 dias úteis.
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {string} planId - ID do plano escolhido
 * @returns {Promise<Object>} - { contractedAt }
 */
export const contractPlan = async (mantenedoraId, planId) => {
  try {
    const response = await api.client.post(
      `api/v1/mantenedoras/${mantenedoraId}/contract`,
      { planId }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = Array.isArray(data)
        ? data[0]
        : (data.errors ? Object.values(data.errors).flat().join(" ") : data.error || data);
      throw new Error(errorMessage || "Erro ao contratar plano");
    }
    throw new Error("Falha na conexão com a API");
  }
};

/**
 * Atualiza o nome da mantenedora e/ou o plano contratado.
 * Apenas Admin/MantenedoraAdmin podem alterar o plano (validado no backend).
 * @param {string} mantenedoraId - ID da mantenedora
 * @param {Object} payload - { name?, planId? }
 * @returns {Promise<Object>} - Resultado da atualização
 */
export const updateMantenedora = async (mantenedoraId, payload) => {
  try {
    const response = await api.client.put(
      `api/v1/mantenedoras/${mantenedoraId}`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = Array.isArray(data)
        ? data[0]
        : (data.errors ? Object.values(data.errors).flat().join(" ") : data.error || data);
      throw new Error(errorMessage || "Erro ao atualizar mantenedora");
    }
    throw new Error("Falha na conexão com a API");
  }
};
